/**
 * Download utilities for handling file downloads from API responses
 */

export interface DownloadResult {
  blob: Blob;
  filename: string;
}

/**
 * Extracts filename from Content-Disposition header
 *
 * Supports formats:
 * - `attachment; filename="example.pdf"`
 * - `attachment; filename*=UTF-8''example.pdf`
 * - `attachment; filename=example.pdf`
 *
 * @param contentDisposition - The Content-Disposition header value
 * @param fallbackFilename - Fallback filename if extraction fails
 * @returns The extracted filename or fallback
 */
export function extractFilenameFromHeader(
  contentDisposition: string | null | undefined,
  fallbackFilename: string = 'download'
): string {
  if (!contentDisposition) {
    return fallbackFilename;
  }

  // Try filename*= (RFC 5987 - UTF-8 encoded)
  const filenameStarMatch = contentDisposition.match(/filename\*=(?:UTF-8''|utf-8'')([^;\s]+)/i);
  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch {
      // Fall through to try other formats
    }
  }

  // Try filename="..." (quoted)
  const filenameQuotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (filenameQuotedMatch?.[1]) {
    return filenameQuotedMatch[1];
  }

  // Try filename=... (unquoted)
  const filenameMatch = contentDisposition.match(/filename=([^;\s]+)/i);
  if (filenameMatch?.[1]) {
    return filenameMatch[1];
  }

  return fallbackFilename;
}

/**
 * Triggers a file download from a blob
 *
 * @param blob - The file blob to download
 * @param filename - The filename to save as
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
